import {
  clearOperatorLockState,
  getOperatorLockStorageKey,
  readOperatorLockState,
  writeOperatorLockState,
  type OperatorLockState,
} from '@shared/utils/operatorLockStorage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const WARNING_BEFORE_MS = 60_000;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'touchstart', 'scroll'];

interface UseOperatorInactivityOptions {
  enabled: boolean;
  timeoutMinutes: number;
  /** ID del usuario para persistir y sincronizar el bloqueo entre pestañas */
  userId?: number | null;
}

interface UseOperatorInactivityResult {
  isLocked: boolean;
  isWarningVisible: boolean;
  secondsRemaining: number;
  extendSession: () => void;
  unlockSession: () => void;
}

function persistLockState(userId: number | null | undefined, state: OperatorLockState): void {
  if (userId == null) return;
  writeOperatorLockState(userId, state);
}

export function useOperatorInactivity({
  enabled,
  timeoutMinutes,
  userId = null,
}: UseOperatorInactivityOptions): UseOperatorInactivityResult {
  const timeoutMs = useMemo(() => Math.max(0, timeoutMinutes) * 60_000, [timeoutMinutes]);
  const warningBeforeMs = useMemo(() => Math.min(WARNING_BEFORE_MS, Math.max(0, timeoutMs - 1_000)), [timeoutMs]);

  const expiresAtRef = useRef<number | null>(null);
  const warningShownRef = useRef(false);
  const isLockedRef = useRef(false);
  const [now, setNow] = useState(() => Date.now());
  const [timerEpoch, setTimerEpoch] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [isWarningVisible, setIsWarningVisible] = useState(false);

  const dismissWarning = useCallback(() => {
    warningShownRef.current = false;
    setIsWarningVisible(false);
  }, []);

  const applyLocked = useCallback(() => {
    expiresAtRef.current = null;
    isLockedRef.current = true;
    dismissWarning();
    setIsLocked(true);
    setTimerEpoch((epoch) => epoch + 1);
    persistLockState(userId, { isLocked: true, expiresAt: null });
  }, [dismissWarning, userId]);

  const startSessionTimeout = useCallback(
    (expiresAtOverride?: number) => {
      const expiresAt = expiresAtOverride ?? Date.now() + timeoutMs;
      expiresAtRef.current = expiresAt;
      isLockedRef.current = false;
      warningShownRef.current = false;
      setIsLocked(false);
      setIsWarningVisible(false);
      setNow(Date.now());
      setTimerEpoch((epoch) => epoch + 1);
      persistLockState(userId, { isLocked: false, expiresAt });
    },
    [timeoutMs, userId],
  );

  const clearSessionTimeout = useCallback(() => {
    expiresAtRef.current = null;
    isLockedRef.current = false;
    dismissWarning();
    setIsLocked(false);
    setTimerEpoch((epoch) => epoch + 1);
    if (userId != null) {
      clearOperatorLockState(userId);
    }
  }, [dismissWarning, userId]);

  const restartSessionTimeout = useCallback(() => {
    if (!enabled || timeoutMs <= 0) {
      clearSessionTimeout();
      return;
    }

    startSessionTimeout();
  }, [clearSessionTimeout, enabled, startSessionTimeout, timeoutMs]);

  const resetTimers = useCallback(() => {
    restartSessionTimeout();
  }, [restartSessionTimeout]);

  const extendSession = useCallback(() => {
    restartSessionTimeout();
  }, [restartSessionTimeout]);

  const unlockSession = useCallback(() => {
    restartSessionTimeout();
  }, [restartSessionTimeout]);

  const handleActivity = useCallback(() => {
    // Con el aviso abierto solo "Prorrogar" debe reiniciar el contador
    if (!enabled || timeoutMs <= 0 || isLockedRef.current || isWarningVisible) {
      return;
    }
    resetTimers();
  }, [enabled, timeoutMs, isWarningVisible, resetTimers]);

  // Restaurar estado persistido o iniciar timeout al activar el monitoreo
  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      clearSessionTimeout();
      return;
    }

    const stored = userId != null ? readOperatorLockState(userId) : null;
    const currentNow = Date.now();

    if (stored?.isLocked) {
      expiresAtRef.current = null;
      isLockedRef.current = true;
      dismissWarning();
      setIsLocked(true);
      setTimerEpoch((epoch) => epoch + 1);
      return;
    }

    if (stored?.expiresAt != null) {
      if (stored.expiresAt <= currentNow) {
        applyLocked();
        return;
      }

      startSessionTimeout(stored.expiresAt);
      return;
    }

    startSessionTimeout();
  }, [applyLocked, clearSessionTimeout, dismissWarning, enabled, startSessionTimeout, timeoutMs, userId]);

  // Sincronizar bloqueo / expiración entre pestañas vía storage events
  useEffect(() => {
    if (!enabled || timeoutMs <= 0 || userId == null) {
      return undefined;
    }

    const storageKey = getOperatorLockStorageKey(userId);

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;

      if (event.newValue == null) {
        // Otra pestaña limpió el estado (p. ej. logout): reiniciar sesión local
        startSessionTimeout();
        return;
      }

      let parsed: OperatorLockState | null = null;
      try {
        parsed = JSON.parse(event.newValue) as OperatorLockState;
      } catch {
        return;
      }

      if (typeof parsed?.isLocked !== 'boolean') return;

      if (parsed.isLocked) {
        expiresAtRef.current = null;
        isLockedRef.current = true;
        dismissWarning();
        setIsLocked(true);
        setTimerEpoch((epoch) => epoch + 1);
        return;
      }

      const expiresAt =
        typeof parsed.expiresAt === 'number' && Number.isFinite(parsed.expiresAt) ? parsed.expiresAt : null;

      if (expiresAt == null || expiresAt <= Date.now()) {
        applyLocked();
        return;
      }

      // Desbloqueo o actividad en otra pestaña: alinear el timer local
      startSessionTimeout(expiresAt);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [applyLocked, dismissWarning, enabled, startSessionTimeout, timeoutMs, userId]);

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      return undefined;
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [enabled, timeoutMs, handleActivity]);

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const currentNow = Date.now();
      setNow(currentNow);

      const expiresAt = expiresAtRef.current;
      if (!expiresAt || isLockedRef.current) {
        return;
      }

      const remainingMs = expiresAt - currentNow;
      if (remainingMs <= 0) {
        applyLocked();
        return;
      }

      if (!warningShownRef.current && remainingMs <= warningBeforeMs) {
        warningShownRef.current = true;
        setIsWarningVisible(true);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [applyLocked, enabled, timeoutMs, warningBeforeMs]);

  const secondsRemaining = useMemo(() => {
    void timerEpoch;
    if (!enabled || isLocked || !expiresAtRef.current) {
      return 0;
    }
    return Math.max(0, Math.ceil((expiresAtRef.current - now) / 1000));
  }, [enabled, isLocked, now, timerEpoch]);

  return {
    isLocked,
    isWarningVisible,
    secondsRemaining,
    extendSession,
    unlockSession,
  };
}
